// src/pages/Login.tsx
import "../styles/utilities.css";
import React, {useEffect, useState, useCallback} from "react";
import {
  Box,
  Text,
  VStack,
  Flex,
  Input, InputGroup,
} from "@chakra-ui/react";
import Logo from "../components/Logo.tsx";
import {useNavigate} from "react-router-dom";
import useAuthStore from "../store/useAuthStore.ts";
import {MdLock} from "react-icons/md";
import {FaUser} from "react-icons/fa";
import {IoEye, IoEyeOff} from "react-icons/io5";
import AppSpinner from "../components/AppSpinner.tsx";
import {getGreeting} from "../utils/utils.ts";

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState(import.meta.env.VITE_USERNAME || "");
  const [password, setPassword] = useState(import.meta.env.VITE_PASSWORD || "");
  const [showPassword, setShowPassword] = useState(false);
  const {loading, login, error, isAuthenticated} = useAuthStore();
  const navigate = useNavigate();

  const handleLogin = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    await login(username, password);
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  const handleKeyDown = useCallback(
    async (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        await login(username, password);
      }
    },
    [username, password, login]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <Flex className="w-full h-full flex flex-col">
      <Flex>
        <Logo/>
      </Flex>
      <Flex className="flex-1 w-full items-center justify-center">
        <VStack spacing={10}>
          <Text className="text-3xl font-bold">👋 {getGreeting()}, Welcome to Glix!</Text>
          <Box className="w-[80%]">
            <VStack spacing={4}>

              {/* Username Input Field */}
              <InputGroup className="login-input-wrapper gap-2">
                {/* Left Icon (Email) - Centered Vertically */}
                <FaUser className="text-loginInputsText-500 text-xl ml-3"/>
                <Input
                  type="text"
                  variant="unstyled"
                  className="login-input-field"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </InputGroup>


              {/*Password Input*/}
              <InputGroup className="login-input-wrapper gap-2">
                {/* Left Icon (Lock) */}
                <MdLock className="text-loginInputsText-500 text-xl ml-3"/>
                {/* Password Input Field */}
                <Input
                  type={showPassword ? "text" : "password"}
                  className="login-input-field"
                  variant="unstyled"
                  placeholder="Your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />

                {/* Right Icon (Eye Toggle) */}

                <button
                  type="button"
                  className="login-icon-button"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <IoEyeOff/> : <IoEye/>}
                </button>

              </InputGroup>

              {error && <Text fontSize="md" textAlign="center" color="red" maxW="sm">
                {error}
              </Text>
              }
              {/*Login button*/}
              <button
                className="login-button disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleLogin}
                disabled={loading}
                aria-disabled={loading}
              >
                {!loading ? "Login" : <AppSpinner size="lg"/>}
              </button>
            </VStack>
          </Box>
        </VStack>
      </Flex>
    </Flex>
  );
};

export default LoginPage;
